import { Component, inject, Inject, signal, ViewChild } from '@angular/core';
import { AbbSidePanelComponent } from '@components/abb-side-panel/abb-side-panel.component';
import {
  SIDE_PANEL_DATA,
  SidePanelData,
} from '@components/abb-side-panel/side-panel';
import { AbbCommonModule } from 'app/abb-common.module';
import { UserFormComponent } from '../user-form/user-form.component';
import { TPipe, TRANSLATION_PREFIX } from '@pipes/t.pipe';
import { FormGroup } from '@angular/forms';
import { canCreateUserQuery, createUserMutation } from '@graphql/user';
import {
  CanCreateUserQuery,
  CreateUserMutation,
  CreateUserMutationVariables,
  PermissionInput,
} from 'gql/graphql';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '@services/ui/toast.service';
import { Apollo } from 'apollo-angular';
import { t } from '@utils/strings';
import { firstValueFrom } from 'rxjs';
import { OverlayService } from '@services/ui/overlay.service';

type UsersSidePanelData = {
  mode: 'new';
};

@Component({
  selector: 'abb-users-side-panel',
  standalone: true,
  template: `
    <abb-side-panel [title]="'New User' | t" #panelRef>
      <abb-user-form
        [mode]="data.mode"
        (formSubmit)="onSubmit($event)"
        (formValidationStatusChange)="handleSubmitStatusBtn($event)"
      ></abb-user-form>
      <div footer class="w-full flex flex-row gap-8 justify-end p-8">
        <abb-button
          variant="clean"
          [disabled]="saving()"
          (onclick)="onCancel()"
          >{{ 'cancel' | t }}</abb-button
        >
        <abb-button
          class="w-32"
          type="submit"
          form="user-form"
          [loading]="saving()"
          [disabled]="submitBtnDisabled || saving()"
          >{{ 'Create' | t }}</abb-button
        >
      </div>
    </abb-side-panel>
  `,
  styles: ``,
  imports: [AbbCommonModule, AbbSidePanelComponent, UserFormComponent, TPipe],
  providers: [{ provide: TRANSLATION_PREFIX, useValue: 'settings.tabs.users' }],
})
export class UsersSidePanelComponent
  implements SidePanelData<UsersSidePanelData>
{
  @ViewChild('panelRef') panelRef: AbbSidePanelComponent | undefined;
  prefix = inject(TRANSLATION_PREFIX);
  constructor(
    @Inject(SIDE_PANEL_DATA) public data: UsersSidePanelData,
    private readonly apollo: Apollo,
    private readonly translate: TranslateService,
    private readonly toastService: ToastService,
    private readonly overlay: OverlayService
  ) {}

  submitBtnDisabled: boolean = true;
  handleSubmitStatusBtn(disabled: boolean): void {
    this.submitBtnDisabled = disabled;
  }

  onCancel() {
    this.panelRef?.dispose();
  }

  saving = signal(false);
  async onSubmit(form: FormGroup): Promise<void> {
    if (form.invalid) {
      return;
    }
    this.saving.set(true);

    const canCreateUserResult = await firstValueFrom(
      this.apollo.query<CanCreateUserQuery>({
        query: canCreateUserQuery,
        variables: { email: form.value.email! },
      })
    );
    if (canCreateUserResult.data.canCreateUser && !canCreateUserResult.data.canCreateUser.canCreate) {
      this.overlay.openAlert({
        title: this.translate.instant('Unable to proceed'),
        message: this.translate.instant('User with this email already exists'),
        showCancelButton: false,
        confirmText: this.translate.instant('Ok'),
      });
      this.saving.set(false);
      return;
    }

    const commissionerPermissionsCtrl = form.get('commissionerPermissions');
    let mappedCommissionerPermissions: CreateUserMutationVariables['commissionerPermissions'];
    if (commissionerPermissionsCtrl && commissionerPermissionsCtrl?.value) {
      mappedCommissionerPermissions = Object.entries(
        commissionerPermissionsCtrl?.value
      ).map(([key, value]) => {
        return {
          key,
          value: value as PermissionInput,
        };
      });
    }
    this.apollo
      .mutate<CreateUserMutation, CreateUserMutationVariables>({
        mutation: createUserMutation,
        variables: {
          name: form.value.name,
          lastName: form.value.lastName,
          email: form.value.email,
          role: form.value.role,
          propertyIds: form.value.propertyIds,
          commissionerEndDate: form.value.commissionerEndDate ?? undefined,
          commissionerPermissions: mappedCommissionerPermissions ?? undefined,
        },
      })
      .subscribe({
        next: () => {
          this.saving.set(false);
          this.toastService.notifySuccess(
            t(this.prefix, `{name} was created successfully`, this.translate, {
              name: form.value.name + ' ' + form.value.lastName,
            })
          );
          this.panelRef?.dispose();
          this.apollo.client.refetchQueries({
            include: ['UserList'],
          });
        },
        error: () => {
          this.saving.set(false);
          this.toastService.notifyError(
            t(
              this.prefix,
              `An error occurred while creating {name}`,
              this.translate,
              { name: form.value.name + ' ' + form.value.lastName }
            )
          );
        },
      });
  }
}
